import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import ComicCard from '@/components/base/ComicCard';
import ArticleCard from '@/components/base/ArticleCard';
import { categories } from '@/mocks/categories';
import { featuredComics, newComics, hotComics, completedComics } from '@/mocks/comics';
import { articles } from '@/mocks/articles';

const heroSlides = [
  {
    id: 1,
    title: '暗夜追踪',
    subtitle: '城市暗处隐藏的秘密，等你来揭开',
    cta: '立即阅读',
    link: '/article/1',
    image: 'https://readdy.ai/api/search-image?query=Dramatic%20noir%20manga%20style%20cityscape%20at%20night%2C%20mysterious%20silhouette%20on%20rooftop%2C%20moonlight%20casting%20long%20shadows%2C%20warm%20amber%20streetlights%2C%20atmospheric%20fog%2C%20dark%20crimson%20accents%2C%20cinematic%20composition%2C%20japanese%20seinen%20manga%20art%20style%2C%20rich%20contrast%2C%20clean%20dramatic%20atmosphere&width=1800&height=900&seq=hero-slide-01&orientation=landscape',
  },
  {
    id: 2,
    title: '绝世神医',
    subtitle: '妙手回春，逆天改命的传奇之路',
    cta: '探索更多',
    link: '/article/10',
    image: 'https://readdy.ai/api/search-image?query=Epic%20fantasy%20manga%20scene%2C%20ancient%20Chinese%20physician%20with%20glowing%20mystical%20energy%2C%20dramatic%20mountain%20landscape%2C%20flowing%20silk%20robes%2C%20warm%20golden%20and%20crimson%20light%2C%20ethereal%20atmosphere%2C%20japanese%20manga%20art%20style%2C%20heroic%20composition%2C%20clean%20background&width=1800&height=900&seq=hero-slide-02&orientation=landscape',
  },
  {
    id: 3,
    title: '江湖风云录',
    subtitle: '荡气回肠的武侠史诗，千万读者的选择',
    cta: '开始追更',
    link: '/article/16',
    image: 'https://readdy.ai/api/search-image?query=Epic%20wuxia%20manga%20landscape%2C%20lone%20warrior%20standing%20on%20cliff%20edge%20overlooking%20vast%20mountain%20range%2C%20dramatic%20sunset%20sky%2C%20flowing%20robes%20in%20wind%2C%20warm%20golden%20and%20amber%20light%2C%20Chinese%20ink%20wash%20painting%20style%2C%20cinematic%20wide%20composition&width=1800&height=900&seq=hero-slide-03&orientation=landscape',
  },
];

const statItems = [
  { label: '注册用户', value: '5000万+', icon: 'ri-user-star-line' },
  { label: '漫画作品', value: '10万+', icon: 'ri-book-open-line' },
  { label: '每日更新', value: '300+', icon: 'ri-refresh-line' },
  { label: '正版授权', value: '100%', icon: 'ri-shield-check-line' },
];

export default function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [activeCategory, setActiveCategory] = useState('all');

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  const filteredComics = activeCategory === 'all'
    ? [...featuredComics, ...hotComics].slice(0, 8)
    : [...featuredComics, ...hotComics, ...newComics, ...completedComics]
        .filter((c) => c.category === categories.find((cat) => cat.slug === activeCategory)?.name)
        .slice(0, 8);

  const siteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '羞羞漫画',
    url: 'https://xiuxiumanhua.com',
    description: '羞羞漫画是专注于成人向漫画内容的正版阅读平台，海量独家漫画资源，支持多端同步阅读。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://xiuxiumanhua.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '羞羞漫画',
    url: 'https://xiuxiumanhua.com',
    logo: 'https://xiuxiumanhua.com/logo.png',
    description: '专注于成人向漫画内容的正版阅读平台',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'kefu@xiuxiumanhua.com',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://xiuxiumanhua.com/' },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="羞羞漫画 - 正版授权漫画平台官网"
        description="羞羞漫画是专注于成人向漫画内容的正版阅读平台，海量独家漫画资源，支持多端同步阅读。在羞羞漫画发现最新连载、热门完结漫画，注册即享免费章节，每日更新不间断，给你最极致的漫画阅读体验。"
        keywords="羞羞漫画,羞羞漫画在线阅读,羞羞漫画最新章节,羞羞漫画官网入口,成人漫画平台,正版漫画阅读,热门漫画推荐"
        canonicalUrl="/"
        ogImage={heroSlides[0].image}
        schemaData={{ ...siteSchema, ...orgSchema, ...breadcrumbSchema }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar transparent />

        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
          {heroSlides.map((slide, index) => (
            <div
              key={slide.id}
              className={`absolute inset-0 transition-opacity duration-800 ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={slide.image}
                alt={slide.title}
                className="w-full h-full object-cover"
              />
            </div>
          ))}
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center px-4 w-full max-w-3xl">
              <h1
                className="text-3xl md:text-5xl font-bold text-white mb-3 md:mb-4"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                {heroSlides[currentSlide].title}
              </h1>
              <p className="text-base md:text-lg text-white/80 mb-6 md:mb-8">
                {heroSlides[currentSlide].subtitle}
              </p>
              <Link
                to={heroSlides[currentSlide].link}
                className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
              >
                {heroSlides[currentSlide].cta}
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>

          {/* Slide indicators */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
            {heroSlides.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentSlide(i)}
                className={`w-8 h-1 rounded-full transition-all cursor-pointer ${
                  i === currentSlide ? 'bg-white w-12' : 'bg-white/40'
                }`}
                aria-label={`切换到第 ${i + 1} 张`}
              />
            ))}
          </div>

          {/* Arrow buttons */}
          <button
            onClick={() => setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length)}
            className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm text-white flex items-center justify-center hover:bg-white/30 transition-all cursor-pointer whitespace-nowrap"
            aria-label="上一张"
          >
            <i className="ri-arrow-left-s-line text-lg"></i>
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm text-white flex items-center justify-center hover:bg-white/30 transition-all cursor-pointer whitespace-nowrap"
            aria-label="下一张"
          >
            <i className="ri-arrow-right-s-line text-lg"></i>
          </button>
        </section>

        {/* Stats Section */}
        <section className="relative -mt-10 z-10">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              {statItems.map((stat) => (
                <div
                  key={stat.label}
                  className="bg-background-50 rounded-xl p-4 md:p-5 border border-background-200/70 text-center"
                >
                  <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-primary-100 flex items-center justify-center">
                    <i className={`${stat.icon} text-primary-500 text-lg`}></i>
                  </div>
                  <p className="text-xl md:text-2xl font-bold text-foreground-950">{stat.value}</p>
                  <p className="text-xs text-foreground-500 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section className="py-10 md:py-14">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                分类浏览
              </h2>
              <Link
                to="/category/all"
                className="text-sm text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="flex flex-wrap gap-2 mb-6">
              {categories.slice(0, 8).map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => setActiveCategory(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {filteredComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            ) : (
              <p className="text-center text-foreground-500 py-10">暂无该分类的漫画，请查看其他分类</p>
            )}
          </div>
        </section>

        {/* Featured Comics Section */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                编辑推荐
              </h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
              {featuredComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          </div>
        </section>

        {/* New Comics Section */}
        <section className="py-10 md:py-14">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                最新上架
              </h2>
              <Link
                to="/category/lianzai"
                className="text-sm text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
              >
                更多新作 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop>
              {newComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          </div>
        </section>

        {/* Hot Comics Section */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                热门排行
              </h2>
              <div className="flex items-center gap-1">
                <span className="w-6 h-6 rounded bg-accent-500 text-background-50 text-xs flex items-center justify-center font-bold">
                  <i className="ri-fire-line"></i>
                </span>
                <span className="text-xs text-foreground-500">火爆连载中</span>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop>
              {hotComics.map((comic, idx) => (
                <div key={comic.id} className="relative">
                  <div className="absolute -top-1 -left-1 z-10 w-7 h-7 rounded-full bg-accent-500 text-background-50 text-xs font-bold flex items-center justify-center">
                    {idx + 1}
                  </div>
                  <ComicCard comic={comic} />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Completed Comics Section */}
        <section className="py-10 md:py-14">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                经典完结
              </h2>
              <Link
                to="/category/wanjie"
                className="text-sm text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
              >
                查看更多 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
              {completedComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          </div>
        </section>

        {/* Articles Section */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                漫画资讯
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {articles.slice(0, 6).map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          </div>
        </section>

        {/* CTA Banner */}
        <section className="relative py-16 md:py-20 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Collection%20of%20manga%20pages%20and%20comic%20panels%20artistically%20arranged%2C%20warm%20crimson%20and%20golden%20color%20gradient%2C%20soft%20dreamy%20atmosphere%2C%20manga%20reading%20aesthetic%2C%20artistic%20abstract%20background%2C%20japanese%20manga%20style%20elements%2C%20clean%20warm%20composition&width=1800&height=600&seq=cta-banner&orientation=landscape"
            alt="漫画阅读"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/40" />
          <div className="relative max-w-[1440px] mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
              加入羞羞漫画，开启你的阅读之旅
            </h2>
            <p className="text-white/70 text-sm md:text-base mb-6 max-w-xl mx-auto">
              注册即享免费章节，海量正版漫画等你来读，每日更新不间断
            </p>
            <Link
              to="/category/all"
              className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-accent-500 text-foreground-950 text-sm font-semibold hover:bg-accent-400 transition-all cursor-pointer whitespace-nowrap"
            >
              立即开始阅读 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}